import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import Icon from './Icon.jsx';

/** A small non-modal settings panel. It overlays the viewport without changing
 * toolbar height; Escape returns focus and outside clicks dismiss it. */
export default function ToolbarPanel({ label, open, onOpenChange, children, className = '', summary }) {
  const id = useId(), trigger = useRef(null), panel = useRef(null), root = useRef(null);
  useLayoutEffect(() => {
    if (!open) return;
    const fit = () => {
      const button = trigger.current.getBoundingClientRect(), base = root.current.getBoundingClientRect(), card = panel.current;
      const scene = root.current.closest('.stage-3d')?.getBoundingClientRect();
      const left = Math.max(0, scene?.left || 0), right = Math.min(window.innerWidth, scene?.right ?? window.innerWidth);
      const top = Math.max(0, scene?.top || 0), bottom = Math.min(window.innerHeight, scene?.bottom ?? window.innerHeight);
      card.style.maxWidth = `${Math.max(0, right - left - 24)}px`;
      const below = Math.max(0, bottom - button.bottom - 18), above = Math.max(0, button.top - top - 18);
      const up = below < Math.min(280, card.scrollHeight) && above > below;
      const x = Math.max(left + 12, Math.min(button.left, right - card.offsetWidth - 12));
      card.style.setProperty('--panel-left', `${x - base.left}px`);
      card.style.setProperty('--panel-space', `${up ? above : below}px`);
      card.style.top = up ? 'auto' : `${button.bottom - base.top + 6}px`;
      card.style.bottom = up ? `${base.bottom - button.top + 6}px` : 'auto';
      card.dataset.placement = up ? 'above' : 'below';
    };
    fit(); panel.current.scrollTop = 0;
    const observer = new ResizeObserver(fit); observer.observe(root.current);
    const scene = root.current.closest('.stage-3d'); if (scene) observer.observe(scene);
    window.addEventListener('resize', fit);
    return () => { observer.disconnect(); window.removeEventListener('resize', fit); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!root.current?.contains(event.target)) onOpenChange(false); };
    const key = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onOpenChange(false); trigger.current?.focus(); }
    };
    const focus = event => { if (!root.current?.contains(event.target)) onOpenChange(false); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key, true);
    document.addEventListener('focusin', focus);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key, true); document.removeEventListener('focusin', focus); };
  }, [open, onOpenChange]);
  return <div ref={root} className={'toolbar-panel ' + className}>
    <button ref={trigger} className="btn sm toolbar-panel-trigger" type="button" aria-label={label} aria-expanded={open} aria-controls={id} onClick={() => onOpenChange(!open)}>{summary || label}<Icon name="chevron-down" size={10} /></button>
    <section ref={panel} id={id} className="toolbar-panel-content" aria-label={label} hidden={!open}>
      <div className="toolbar-panel-heading"><strong>{label}</strong><button className="btn sm" aria-label={`Close ${label.toLowerCase()}`} onClick={() => { onOpenChange(false); trigger.current?.focus(); }}>Close</button></div>
      {children}
    </section>
  </div>;
}
